import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import {
  getBookNarrationEstimate,
  getBookNarrationRun,
  narrateBook,
  type BookGroup,
  type BookRun,
  type Chapter,
} from "../api";
import MutationError from "../components/MutationError";
import { formatMicros } from "./money";
import { useT, type Messages } from "../i18n";

/**
 * NarrateAll brings the whole book onto its settings, confirmed first
 * (ADR-0119). The button opens a confirmation listing what it would do, in
 * groups: chapters never narrated and chapters whose text changed are always
 * done; chapters recorded on other settings — another model, voice, part size —
 * are groups of their own, ticked by default and each one untickable, because
 * redoing them re-records whole chapters at full price. Chapters whose text is
 * unchanged but voiced differently today are one more group, unticked until
 * the author ticks it: their audio is fine, and re-buying it is "a waste of
 * money" (ADR-0179 §5).
 *
 * Confirming hands the chapters to a job; the run it returns is what this
 * watches, so a press that stopped part-way says so instead of reading as done.
 */
export default function NarrateAll({ bookID, items }: { bookID: string; items: Chapter[] }) {
  const t = useT();
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  // Groups the author unticked, by key. Unticked rather than ticked, so a
  // group that appears while the confirmation is open starts included, like
  // every other.
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  // Opt-in groups the author ticked, by key.
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const chosen = (g: BookGroup) =>
    g.kind === "revoiced" ? picked.has(g.key) : g.kind !== "other_settings" || !skipped.has(g.key);
  const toggle = (g: BookGroup, on: boolean) => {
    const set = g.kind === "revoiced" ? setPicked : setSkipped;
    set((prev) => {
      const next = new Set(prev);
      if (on === (g.kind === "revoiced")) next.add(g.key);
      else next.delete(g.key);
      return next;
    });
  };
  const narrating = items.filter((c) => c.narrating).length;
  const estimate = useQuery({
    queryKey: ["book-narration", bookID],
    queryFn: () => getBookNarrationEstimate(bookID),
    retry: false,
    // Refreshed as the rows change: a chapter finishing narrating leaves the count.
    staleTime: 0,
  });
  const run = useQuery({
    queryKey: ["book-narration-run", bookID],
    queryFn: async () => (await getBookNarrationRun(bookID)) ?? null,
    retry: false,
    refetchInterval: (query) => (query.state.data?.status === "running" ? 2000 : false),
  });
  const status = run.data?.status;
  // When a run settles, the chapters it started are narrating: the rows and
  // the estimate both change.
  const lastStatus = useRef(status);
  useEffect(() => {
    if (lastStatus.current === "running" && status !== "running") {
      void client.invalidateQueries({ queryKey: ["chapters", bookID] });
      void client.invalidateQueries({ queryKey: ["book-narration", bookID] });
    }
    lastStatus.current = status;
  }, [status, bookID, client]);

  const e = estimate.data;
  const included = (e?.groups ?? []).filter(chosen);
  const chosenChapters = included.reduce((sum, g) => sum + g.chapters, 0);
  const chosenMicros = included.reduce((sum, g) => sum + g.estimate_micros, 0);

  const start = useMutation({
    mutationFn: () =>
      narrateBook(
        bookID,
        included.filter((g) => optional(g)).map((g) => g.key),
      ),
    onSuccess: (started) => {
      setOpen(false);
      setSkipped(new Set());
      setPicked(new Set());
      client.setQueryData(["book-narration-run", bookID], started);
    },
    // A 409 means the book changed under the confirmation: show it again as
    // it is now, with the server's sentence above it.
    onError: () => void client.invalidateQueries({ queryKey: ["book-narration", bookID] }),
  });

  if (!e) return null;
  const nothing = e.chapters === 0 && narrating === 0;
  const running = status === "running";
  const hasOther = e.groups.some((g) => g.kind === "other_settings");

  return (
    <div className="mt-4 text-xs text-muted" data-testid="narrate-all">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {e.chapters > 0 && !open && !running && (
          <button
            type="button"
            data-testid="narrate-all-button"
            onClick={() => {
              start.reset();
              setOpen(true);
              // What is about to be confirmed is the book now, not when the
              // page last priced it: a chapter added since is on the list.
              void estimate.refetch();
            }}
            className="rounded-control border border-line px-3 py-1.5 text-sm text-ink transition-colors hover:bg-sunk"
          >
            {t.narration.narrateAll}
          </button>
        )}
        {!open && (
          <span data-testid="narrate-all-line">
            {run.data && worthSaying(run.data)
              ? runLine(t, run.data)
              : narrating > 0
                ? t.narration.narrateAllStarted(narrating)
                : nothing
                  ? t.narration.narrateAllNothing
                  : t.narration.narrateAllEstimate(e.chapters, e.estimate_display)}
          </span>
        )}
      </div>

      {open && (
        <div
          role="group"
          aria-label={t.narration.narrateAllTitle}
          data-testid="narrate-all-confirm"
          className="mt-2 max-w-md rounded-card border border-line bg-surface p-3 text-sm text-ink"
        >
          <p className="font-medium">{t.narration.narrateAllTitle}</p>
          <ul className="mt-2 space-y-1.5">
            {e.groups.map((g) =>
              optional(g) ? (
                <li key={g.key}>
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      data-testid="narrate-all-group"
                      checked={chosen(g)}
                      onChange={(ev) => toggle(g, ev.target.checked)}
                      className="mt-0.5 accent-sage"
                    />
                    <GroupLine t={t} group={g} />
                  </label>
                </li>
              ) : (
                <li key={g.key} className="flex items-start gap-2">
                  <span aria-hidden className="mt-0.5 inline-block w-[13px] text-center">
                    ·
                  </span>
                  <GroupLine t={t} group={g} />
                </li>
              ),
            )}
          </ul>
          {hasOther && (
            <p className="mt-2 text-xs text-muted">
              {t.narration.narrateAllRedoNote(e.model_label, e.voice_label)}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="narrate-all-go"
              disabled={start.isPending || chosenChapters === 0}
              onClick={() => start.mutate()}
              className="rounded-control bg-sage px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-sage-deep disabled:opacity-40"
            >
              {t.narration.narrateAllConfirm(chosenChapters)}{" "}
              {/* The amount is isolated left to right, or a Persian line puts
                  the dollar sign on the wrong end (05 §Web client). */}
              <bdi dir="ltr">{formatMicros(chosenMicros)}</bdi>
            </button>
            <button
              type="button"
              data-testid="narrate-all-cancel"
              onClick={() => {
                setOpen(false);
                setSkipped(new Set());
                setPicked(new Set());
              }}
              className="rounded-control px-2 py-1.5 text-sm text-ink-soft transition-colors hover:bg-sunk"
            >
              {t.narration.narrateAllCancel}
            </button>
          </div>
        </div>
      )}
      <MutationError
        error={start.error}
        fallback={t.narration.narrateAllFailed}
        className="mt-1 text-xs"
      />
    </div>
  );
}

/** A group the confirmation lets the author tick or untick (ADR-0119, 0179). */
function optional(g: BookGroup): boolean {
  return g.kind === "other_settings" || g.kind === "revoiced";
}

function GroupLine({ t, group: g }: { t: Messages; group: BookGroup }) {
  let what: string;
  switch (g.kind) {
    case "not_narrated":
      what = t.narration.narrateAllNotNarrated(g.chapters);
      break;
    case "text_changed":
      what = t.narration.narrateAllTextChanged(g.chapters);
      break;
    case "refinish":
      what = t.narration.narrateAllRefinish(g.chapters);
      break;
    case "revoiced":
      what = t.narration.narrateAllRevoiced(g.chapters);
      break;
    default: {
      const differs = g.differs ?? [];
      what =
        differs.includes("model") || differs.includes("voice")
          ? t.narration.narrateAllRecordedOn(g.chapters, g.model_label ?? "", g.voice_label ?? "")
          : t.narration.narrateAllRecordedWith(
              g.chapters,
              differs
                .map((d) => t.narration.narrateAllSetting[d])
                .join(t.narration.narrateAllSettingSeparator),
            );
    }
  }
  return (
    <span data-testid={`narrate-all-${g.kind}`}>
      {what}
      {/* The amount is isolated left to right inside a line that may be laid
          out right to left (05 §Web client). */}
      <span className="ms-1.5 text-muted">
        <bdi dir="ltr">{g.estimate_display}</bdi>
      </span>
    </span>
  );
}

/**
 * A running press is always the line. A settled one is news only when it did
 * not do everything it was asked, and only for a while; after a clean finish
 * the chapter rows and the estimate say more.
 */
function worthSaying(run: BookRun): boolean {
  if (run.status === "running") return true;
  const recent = Date.now() - new Date(run.created_at).getTime() < 30 * 60 * 1000;
  return recent && (run.status !== "done" || run.not_started > 0);
}

function runLine(t: Messages, run: BookRun): string {
  switch (run.status) {
    case "running":
      return t.narration.narrateAllRunning(run.done, run.planned);
    case "done":
      return t.narration.narrateAllDone(run.started, run.not_started);
    case "stopped":
      return run.reason === "no_budget"
        ? t.narration.narrateAllStoppedNoBudget
        : t.narration.narrateAllStoppedBudget(run.started, run.planned);
    default:
      return t.narration.narrateAllRunFailed(run.started, run.planned);
  }
}
